import { getApiUrl } from "../utils/envConfig";
import {
  validateSecurityHeaders,
  getApiSecurityHeaders,
} from "../config/securityHeaders";
import { getAuthToken, refreshAuthToken } from "../utils/authUtils";
import { fetchUserData } from "./authApi";
import axios from "axios";
import { extractClientId } from "@web/utils/extractClientId";
import { emptyProductScopes } from "../components/productScopes";

// Enhanced axios utility with token management and retry logic
export const makeAuthenticatedRequest = async (
  url,
  options = {},
  retries = 3
) => {
  try {
    // Always ensure we have a valid token before making the request
    const token = await getAuthToken();

    const headers = {
      "content-type": "application/json",
      Authorization: `Bearer ${token}`,
      ...getApiSecurityHeaders(), // Add security headers
      ...options.headers,
    };

    const requestConfig = {
      url,
      headers,
      method: options.method || "GET",
      ...options,
    };

    // Handle data/body for axios
    if (options.body) {
      requestConfig.data = JSON.parse(options.body);
      delete requestConfig.body;
    }
    const response = await axios(requestConfig);

    // Validate security headers in development
    if (import.meta.env.VITE_ENVIRONMENT === "development") {
      validateSecurityHeaders(response);
    }

    return response.data;
  } catch (error) {
    // Check if this is an authentication error (401 or 403)
    const isAuthError = error.response && (error.response.status === 401 || error.response.status === 403);

    // Only retry on authentication errors
    if (isAuthError && retries > 0) {
      try {
        await refreshAuthToken();
        // Retry the request with the new token
        return await makeAuthenticatedRequest(url, options, retries - 1);
      } catch (tokenError) {
        throw new Error("Failed to refresh token: " + tokenError.message);
      }
    }

    // For non-auth errors or max retries reached, throw the original error.
    if (error.response) {
      // Surface FastAPI's `detail` first — it's the field contractually meant
      // for the client (e.g. "User not found" on a 404). Without it, a 4xx fell
      // back to the raw axios message ("Request failed with status code 404").
      // Only a string `detail` is shown: 422 returns an array of error objects,
      // which would stringify to noise (mirrors authApi.js's guard).
      const detail = error.response.data?.detail;
      throw new Error(
        (typeof detail === "string" && detail) ||
          error.response.data?.message ||
          error.response.data?.error ||
          error.message
      );
    } else {
      // Network or other error
      throw error;
    }
  }
};

// Fetch users for user management page
export const fetchUsersDetailed = async (
  existingUserData = null,
  selectedWorkspace = null
) => {
  try {
    let userData, idClient;

    if (existingUserData) {
      // Use existing user data from Redux
      userData = existingUserData;
      idClient = extractClientId(userData, selectedWorkspace);
    } else {
      // Fallback to fetching user data
      try {
        userData = await fetchUserData(true);
        idClient = extractClientId(userData, selectedWorkspace);
      } catch (userDataError) {
        console.error("[fetchUsersDetailed] Error fetching user data:", userDataError);
        throw new Error("Failed to fetch user authentication data: " + userDataError.message);
      }
    }

    if (!idClient) {
      console.error("[fetchUsersDetailed] No client ID found, userData:", userData);
      throw new Error("Client ID not found in user data");
    }

    // Fetch user management data from the new endpoint
    const url = `${getApiUrl()}/clients/${idClient}/users/detailed`;

    try {
      const data = await makeAuthenticatedRequest(url);

      return {
        users: data.users || [],
        totalCount: data.total_count || 0,
        client: data.client || idClient,
      };
    } catch (apiError) {
      console.error("[fetchUsersDetailed] API request failed:", apiError);
      throw new Error("API request failed: " + apiError.message);
    }
  } catch (error) {
    console.error("[fetchUsersDetailed] Overall error:", error);
    throw new Error("Failed to fetch users detailed: " + error.message);
  }
};

// Fetch users grouped by market for Market View
export const fetchUsersByMarket = async (
  existingUserData = null,
  selectedWorkspace = null
) => {
  try {
    let userData, idClient;
    if (existingUserData) {
      // Use existing user data from Redux
      userData = existingUserData;
      idClient = extractClientId(userData, selectedWorkspace);
    } else {
      try {
        userData = await fetchUserData(true);
        idClient = extractClientId(userData, selectedWorkspace);
      } catch (userDataError) {
        throw new Error(
          "Failed to fetch user authentication data: " + userDataError.message
        );
      }
    }

    if (!idClient) {
      throw new Error("Client ID not found in user data");
    }

    // Fetch market-grouped users from the new endpoint
    const url = `${getApiUrl()}/clients/${idClient}/users/by-market`;

    try {
      const data = await makeAuthenticatedRequest(url);

      return {
        marketGroups: data.market_groups || [],
        totalMarkets: data.total_markets || 0,
        totalUsers: data.total_users || 0,
        client: data.client || idClient,
      };
    } catch (apiError) {
      throw new Error("API request failed: " + apiError.message);
    }
  } catch (error) {
    throw new Error("Failed to fetch users by market: " + error.message);
  }
};

// Transform API response to match table format expectations
export const transformUsersForTable = (usersApiData) => {
  const { users, totalCount, client } = usersApiData;

  // First, group users by userId to calculate proper indices for rowSpan calculation
  const usersByUserId = {};
  users.forEach((user) => {
    if (!usersByUserId[user.id_user]) {
      usersByUserId[user.id_user] = [];
    }
    usersByUserId[user.id_user].push(user);
  });

  // Calculate roleIndex and productIndex for each user's records
  const userRoleProductMap = {};
  Object.keys(usersByUserId).forEach((userId) => {
    const userRecords = usersByUserId[userId];
    userRoleProductMap[userId] = {};

    // Group by role
    const roleGroups = {};
    userRecords.forEach((record) => {
      const roleKey = record.role || 'default';
      if (!roleGroups[roleKey]) {
        roleGroups[roleKey] = [];
      }
      roleGroups[roleKey].push(record);
    });

    // Assign roleIndex and productIndex
    let roleIndex = 0;
    Object.keys(roleGroups).forEach((roleKey) => {
      const roleRecords = roleGroups[roleKey];
      userRoleProductMap[userId][roleKey] = {
        roleIndex,
        products: {},
      };

      // Group by module within this role
      let productIndex = 0;
      roleRecords.forEach((record) => {
        const productKey = record.module || 'default';
        if (!userRoleProductMap[userId][roleKey].products[productKey]) {
          userRoleProductMap[userId][roleKey].products[productKey] = productIndex++;
        }
      });

      roleIndex++;
    });
  });

  // Generate stable keys and assign proper indices
  const transformedUsers = users.map((user, index) => {
    // Create a stable key using id_user, module, and market
    const normalizeForKey = (str) => {
      if (!str) return `item-${index}`;
      return str.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_-]/g, '');
    };

    const productPart = normalizeForKey(user.module);
    const marketPart = normalizeForKey(user.market);
    const stableKey = `${user.id_user}-${productPart}-${marketPart}`;

    // Get roleIndex and productIndex from the calculated map
    const roleKey = user.role || 'default';
    const productKey = user.module || 'default';
    const roleInfo = userRoleProductMap[user.id_user]?.[roleKey];
    const roleIndex = roleInfo?.roleIndex ?? 0;
    const productIndex = roleInfo?.products[productKey] ?? 0;

    // Calculate totals for rowSpan calculation
    const totalRolesForUser = Object.keys(userRoleProductMap[user.id_user] || {}).length;
    const totalProductsForRole = Object.keys(roleInfo?.products || {}).length;

    return {
      key: stableKey,
      userId: user.id_user,
      userKey: `user-${user.id_user}`,

      // User information
      name: user.name,
      email: user.email,
      avatar: null,
      status: "Active",
      lastLogin: null,

      // Role information
      roleId: user.role?.toLowerCase().replace(/\s+/g, '-') || 'user',
      roleName: user.role || 'User',
      role: user.role || 'User',

      // Module information (displayed in Product column)
      productName: user.module || 'All modules',
      product: user.module || 'All modules',

      // Market information
      markets: user.market ? user.market.split('; ') : ['All Markets'],
      market: user.market || 'All Markets',

      // Additional metadata
      client: client,
      permissions: user.module ? [user.module] : ['All'],

      // Invitation lifecycle — threaded through so the Resend Invitation
      // action in useUserTableColumns.jsx can gate on it. Without this
      // line the field arrives from the dashone API but gets stripped here,
      // so the Resend button never renders.
      invitation_status: user.invitation_status ?? null,

      // Properly calculated indices for rowSpan calculation
      roleIndex,
      productIndex,
      totalRolesForUser,
      totalProductsForRole,
    };
  });

  // Sort by userId, then roleIndex, then productIndex for consistent display
  transformedUsers.sort((a, b) => {
    if (a.userId !== b.userId) {
      return a.userId.localeCompare(b.userId);
    }
    if (a.roleIndex !== b.roleIndex) {
      return a.roleIndex - b.roleIndex;
    }
    return a.productIndex - b.productIndex;
  });

  return {
    users: transformedUsers,
    totalCount,
    client,
  };
};

// Country name to code mapping
const COUNTRY_NAME_TO_CODE = {
  "United States": "US",
  USA: "US",
  US: "US",
  France: "FR",
  Spain: "ES",
  Germany: "DE",
  Italy: "IT",
  "United Kingdom": "UK",
  UK: "UK",
  Netherlands: "NL",
  "South Korea": "KR",
  "Hong Kong": "HK",
  Japan: "JP",
  Australia: "AU",
  "New Zealand": "NZ",
  Brazil: "BR",
  Mexico: "MX",
  Colombia: "CO",
  Chile: "CL",
  Argentina: "AR",
  Peru: "PE",
  Canada: "CA",
  "Saudi Arabia": "SA",
  UAE: "AE",
  Turkey: "TR",
  Egypt: "EG",
  Morocco: "MA",
  Tunisia: "TN",
  Algeria: "DZ",
  Nigeria: "NG",
  Kenya: "KE",
  "South Africa": "ZA",
  Indonesia: "ID",
  Cambodia: "KH",
  Myanmar: "MM",
  Malaysia: "MY",
  Philippines: "PH",
  Singapore: "SG",
  Thailand: "TH",
  Vietnam: "VN",
  India: "IN",
};

// Convert country name to country code
const getCountryCode = (name) => COUNTRY_NAME_TO_CODE[name] || name;

// Convert market names to country codes
const convertMarketsToCountryCodes = (markets) => {
  if (!markets || !Array.isArray(markets)) return [];
  return markets.map(getCountryCode);
};

/**
 * Update user's product-based scope configuration
 * PUT /users/{id_user}/scope
 */
export const updateUserScope = async (idUser, scopeData) => {
  if (!idUser) {
    throw new Error("User ID is required");
  }

  const url = `${getApiUrl()}/users/${idUser}/scope`;

  try {
    const data = await makeAuthenticatedRequest(url, {
      method: "PUT",
      body: JSON.stringify(scopeData),
    });

    return data;
  } catch (error) {
    throw new Error("Failed to update user scope: " + error.message);
  }
};

/**
 * Build scope data from user editing form
 */
export const buildUserScopeData = (
  user,
  stagedChanges,
  isSuperAdmin = false
) => {
  // Get the final markets (staged or original)
  const markets = stagedChanges?.markets || user.markets || [];

  // Convert market names to country codes
  const marketCodes = convertMarketsToCountryCodes(markets);

  // Get the product type
  const product = stagedChanges?.product || user.productName || "All products";

  // Build product_scopes based on product selection (per-module keys)
  const productScopes = emptyProductScopes();

  // If super admin, product_scopes will be ignored by backend
  if (!isSuperAdmin) {
    // Everyone gets physical scopes for their markets
    productScopes.physical_location = marketCodes;
    productScopes.physical_franchise = marketCodes;

    switch (product) {
      case "All products":
        productScopes.market_overview = marketCodes;
        productScopes.market_deepdive = marketCodes;
        productScopes.execution_view = marketCodes;
        break;
      case "Market Lens":
        productScopes.market_overview = marketCodes;
        productScopes.market_deepdive = marketCodes;
        break;
      case "Execution Lens":
        productScopes.execution_view = marketCodes;
        break;
      default:
        productScopes.market_overview = marketCodes;
        productScopes.market_deepdive = marketCodes;
        productScopes.execution_view = marketCodes;
    }
  }

  return {
    is_super_admin: isSuperAdmin,
    product_scopes: productScopes,
  };
};

/**
 * Hard delete a user permanently from both Firestore and Firebase Authentication
 * DELETE /users/hard?email={email}
 *
 * This function accepts an email address and directly calls the delete endpoint
 */
export const hardDeleteUser = async (email) => {
  if (!email) {
    throw new Error("User email is required");
  }

  try {
    // Call the hard delete endpoint with email as query parameter
    const url = `${getApiUrl()}/users/hard?email=${encodeURIComponent(email)}`;

    const data = await makeAuthenticatedRequest(url, {
      method: "DELETE",
    });

    return data;
  } catch (error) {
    console.error("[hardDeleteUser] Error:", error);
    throw new Error("Failed to delete user: " + error.message);
  }
};

/**
 * Soft delete (deactivate) a user — reversible.
 * DELETE /users/soft?email={email}
 */
export const deactivateUser = async (email) => {
  if (!email) {
    throw new Error("User email is required");
  }
  try {
    const url = `${getApiUrl()}/users/soft?email=${encodeURIComponent(email)}`;
    const data = await makeAuthenticatedRequest(url, { method: "DELETE" });
    return data;
  } catch (error) {
    console.error("[deactivateUser] Error:", error);
    throw new Error("Failed to deactivate user: " + error.message);
  }
};

/**
 * Reactivate a soft-deleted user — clears delete_time.
 * POST /users/reactivate?email={email}
 */
export const reactivateUser = async (email) => {
  if (!email) {
    throw new Error("User email is required");
  }
  try {
    const url = `${getApiUrl()}/users/reactivate?email=${encodeURIComponent(email)}`;
    const data = await makeAuthenticatedRequest(url, { method: "POST" });
    return data;
  } catch (error) {
    console.error("[reactivateUser] Error:", error);
    throw new Error("Failed to reactivate user: " + error.message);
  }
};

/**
 * Grant or revoke a user's user-level dashmote_admin flag — the /admin gate
 * PUT /users/{idUser}/dashmote-admin { dashmote_admin }
 *
 * Dashmote-admin only (backend-enforced). The backend also rejects targets
 * whose email is not @dashmote.com (400) and audits every change. Only the
 * boolean travels from the client.
 */
export const setUserDashmoteAdmin = async (idUser, dashmoteAdmin) => {
  if (!idUser) {
    throw new Error("User ID is required");
  }
  const url = `${getApiUrl()}/users/${idUser}/dashmote-admin`;
  try {
    const data = await makeAuthenticatedRequest(url, {
      method: "PUT",
      body: JSON.stringify({ dashmote_admin: dashmoteAdmin }),
    });
    return data;
  } catch (error) {
    throw new Error("Failed to update Dashmote admin flag: " + error.message);
  }
};

/**
 * Set a user's account_type, or clear it.
 * PUT /users/{idUser}/account-type { account_type }
 *
 * account_type categorizes non-normal users: "demo" (a @dashmote.com identity
 * excluded from Dashmote-staff implicit all-workspace access — scoped to its
 * assigned workspaces only) or "test" (a staff-created fixture hidden from
 * client user lists). Pass null to clear. Dashmote-admin only
 * (backend-enforced); the backend rejects "demo" on non-@dashmote.com targets
 * (400) and audits every change.
 */
export const setUserAccountType = async (idUser, accountType) => {
  if (!idUser) {
    throw new Error("User ID is required");
  }
  const url = `${getApiUrl()}/users/${idUser}/account-type`;
  try {
    const data = await makeAuthenticatedRequest(url, {
      method: "PUT",
      body: JSON.stringify({ account_type: accountType }),
    });
    return data;
  } catch (error) {
    throw new Error("Failed to update account_type: " + error.message);
  }
};

/**
 * Admin-driven password reset.
 * POST /users/admin-reset-password { email, delivery }
 *
 * Dashmote-staff only (backend-enforced). Generates a one-time temp password.
 * delivery="email" sends it to the user; delivery="copy" returns it in the
 * response (`temp_password`) for the admin to share via a secure channel.
 *
 * Uses a direct authenticated request (not makeAuthenticatedRequest) so the
 * FastAPI `detail` is surfaced verbatim — e.g. the Google-only / soft-deleted
 * rejection reasons — instead of a generic axios message.
 */
export const resetUserPassword = async (email, delivery = "email") => {
  if (!email) {
    throw new Error("User email is required");
  }
  try {
    const token = await getAuthToken();
    const response = await axios({
      method: "POST",
      url: `${getApiUrl()}/users/admin-reset-password`,
      headers: {
        "content-type": "application/json",
        Authorization: `Bearer ${token}`,
        ...getApiSecurityHeaders(),
      },
      data: { email, delivery },
    });
    return response.data;
  } catch (error) {
    const detail = error.response?.data?.detail;
    throw new Error(
      typeof detail === "string" && detail
        ? detail
        : "Failed to reset user password. Please try again."
    );
  }
};
